import { useCallback, useEffect, useState } from 'react';
import { Button, Chip, Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  createExporterDivision,
  getExporterDivisions,
  renameExporterDivision,
  type ExporterDivision,
} from 'common/src/api/exporter-divisions';

const errorMessage = (error: unknown) => {
  const code = (error as { code?: string } | null)?.code;
  if (code === '23505') return 'Ya existe una división con ese nombre.';
  if (code === '42501') return 'Solo un superadmin puede editar divisiones.';
  return 'No se pudo guardar la división.';
};

/** Divisions (business units) of one exporter: list, rename, add. No delete:
 *  the default division and any referenced one cannot go. */
export default function ExporterDivisionsEditor({
  exporterId,
}: {
  exporterId: number;
}) {
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [newName, setNewName] = useState('');
  const [busyId, setBusyId] = useState<number | 'new' | null>(null);

  const load = useCallback(async () => {
    try {
      setDivisions(await getExporterDivisions(exporterId));
    } catch (error) {
      console.error('Error loading divisions:', error);
      toast.error('Error al cargar las divisiones');
    }
  }, [exporterId]);

  useEffect(() => {
    setDrafts({});
    setNewName('');
    void load();
  }, [load]);

  const rename = async (division: ExporterDivision) => {
    const name = (drafts[division.id] ?? division.name).trim();
    if (!name || name === division.name) return;
    setBusyId(division.id);
    try {
      await renameExporterDivision(division.id, name);
      setDrafts((current) => {
        const next = { ...current };
        delete next[division.id];
        return next;
      });
      await load();
      toast.success('División actualizada');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusyId('new');
    try {
      await createExporterDivision(exporterId, name);
      setNewName('');
      await load();
      toast.success('División creada');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='mt-4 flex flex-col gap-3'>
      <div>
        <h3 className='text-sm font-medium'>Divisiones</h3>
        <p className='text-xs text-default-500'>
          Unidades de negocio dentro de la cuenta. Con más de una, despachos y
          liquidaciones se cargan y filtran por división.
        </p>
      </div>
      <div className='flex flex-col gap-2'>
        {divisions.map((division) => {
          const draft = drafts[division.id] ?? division.name;
          const changed = draft.trim() !== division.name && draft.trim() !== '';
          return (
            <div key={division.id} className='flex items-center gap-2'>
              <Input
                size='sm'
                aria-label={`Nombre de la división ${division.name}`}
                value={draft}
                onValueChange={(value) =>
                  setDrafts((current) => ({ ...current, [division.id]: value }))
                }
                isDisabled={busyId !== null}
                endContent={
                  division.is_default ? (
                    <Chip size='sm' variant='flat'>
                      Por defecto
                    </Chip>
                  ) : null
                }
              />
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label='Guardar nombre'
                isDisabled={!changed || busyId !== null}
                isLoading={busyId === division.id}
                onPress={() => void rename(division)}
              >
                <Icon icon='tabler:check' />
              </Button>
            </div>
          );
        })}
      </div>
      <div className='flex items-center gap-2'>
        <Input
          size='sm'
          label='Nueva división'
          value={newName}
          onValueChange={setNewName}
          isDisabled={busyId !== null}
        />
        <Button
          size='sm'
          variant='flat'
          startContent={<Icon icon='tabler:plus' />}
          isDisabled={!newName.trim() || busyId !== null}
          isLoading={busyId === 'new'}
          onPress={() => void add()}
        >
          Agregar
        </Button>
      </div>
    </div>
  );
}
